import { GridStack } from "gridstack";
import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { Button, Col, Container, Row } from "react-bootstrap";
import { useHistory } from "react-router";

import HoverAuthorizeTooltip from "components/authorize/AuthorizeTooltip";
import ContentWrapper from "components/content-wrapper/ContentWrapper";
import { DashboardHeader } from "components/dashboard/DashboardHeader";
import Widget from "components/dashboard/widgets/Widget";
import { CELL_HEIGHT } from "components/hoc/DashboardGridStack";
import { BasicPlanModal } from "components/modals/BasicPlanModal";
import { DashboardModal } from "components/modals/DashboardModal";
import { APP_CONFIG } from "config/appConfig";
import {
    CREATED_DASHBOARD_MESSAGE,
    FETCH_CHART_FAIL_MESSAGE,
    FETCH_DASHBOARD_FAIL_MESSAGE,
    FormType,
    HttpStatus,
} from "constant";
import { AppContext } from "context/appContext";
import { ChartAttribute, Dashboard as DashboardModel } from "generated/models";
import { useBasicPlanModal } from "hooks/useBasicPlanModal";
import { useCheckIfBasicPlan } from "hooks/useCheckIfBasicPlan";
import { WalletAlertComponent, walletModalTrigger } from "hooks/wallet";
import { find } from "lodash";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import dashboardService from "service/dashboardService";
import {
    getAllRegistry,
    getGatewayAlertNotification,
} from "service/gatewayService";
import LocalStorageService from "service/localStorageService";
import { storeDashboardAction } from "store/actions";
import { Alert } from "types";
import { getAPIError, showErrorAlert, showSuccessAlert } from "utils/alert";
import { canAccess } from "utils/authorize-action";
import { isHttpSuccess, tagOptions } from "utils/functions";
import GridStackElement, { GridStackChangeEvent } from "utils/gridstack";

export const NUMBER_OF_SCROLL = 100;

const Dashboard: React.FC = () => {
    const history = useHistory();
    const gridStackRef = useRef<any>();
    const { IS_BASIC_PLAN } = useCheckIfBasicPlan();

    const {
        storeData: {
            organization: { currentOrgId },
            dashboard,
            systemPermissions: { permissions },
        },
        storeDispatchActions,
    } = useContext(AppContext);
    const { dashboardList, currentDashboard } = dashboard;
    const [zoomLevel, setZoomLevel] = useState(window.devicePixelRatio);
    const [isForbiddenResource, setIsForbiddenResource] = useState(false);
    const [isDashboardLoading, setIsDashboardLoading] = useState(true);
    const [isPanelLoading, setIsPanelLoading] = useState(true);
    const [infinityState, setInfinityState] = useState<{
        items: any;
        hasMore: null | boolean;
        count: null | number;
        page: number;
    }>({
        items: [],
        hasMore: null,
        count: null,
        page: 0,
    });
    const [showDashboardForm, setShowDashboardForm] = useState(false);
    const [formType, setFormType] = useState<FormType>(FormType.DEFAULT);
    const { toggleShowBasicPlanModal, showBasicPlanModal }: any =
        useBasicPlanModal();
    const [grid, setGrid] = useState<GridStack | null>(null);
    const [gatewaysConfig, setGatewaysConfig] = useState({});
    const [allRegistry, setAllRegistry] = useState(null);
    const [allAlerts, setAllAlerts] = useState<Alert[]>([]);
    const [jsonListLoaded, setJsonListLoaded] = useState(
        LocalStorageService.getItem("listOfJSON") !== ""
    );
    const isMountedRef = useRef(true);

    // Add this useEffect to set/unset the mounted flag
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        (async () => {
            if (!currentOrgId) {
                return;
            }

            const response = await getAllRegistry(currentOrgId);

            if (!isMountedRef.current) {
                return;
            }

            if (isHttpSuccess(response.status)) {
                const gatewaysConfigObj = response.data.reduce(
                    (acc: any, curr: any) => {
                        acc[curr.gateway_id] = curr.configs.map(
                            (config: any, index: number) => ({
                                ...(find(
                                    curr.info.devices,
                                    (d) => d.UID === config.UID
                                ) ?? {}),
                                ...config,
                                gatewayName: curr.name,
                            })
                        );
                        return acc;
                    },
                    {}
                );
                setGatewaysConfig(gatewaysConfigObj);
                setAllRegistry(response.data);
            }
        })();
    }, [currentOrgId]);

    useEffect(() => {
        (async () => {
            const gatewayList = Object.keys(gatewaysConfig);
            const promiseArray = gatewayList.map((gatewayId: string) => {
                return getGatewayAlertNotification(gatewayId);
            });

            const response = await Promise.all(promiseArray);

            if (!isMountedRef.current) {
                return;
            }

            const consolidatedResponses = response?.reduce(
                (acc: any, curr: any) => {
                    // In On-prem, gateway alert api return null
                    if (curr?.data) {
                        acc.push(...curr.data);
                    }
                    return acc;
                },
                [] as Alert[]
            );
            setAllAlerts(consolidatedResponses);
        })();
    }, [gatewaysConfig]);

    const hasPermission = useMemo(
        () => canAccess("dashboard:read") && !isForbiddenResource,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [permissions, isForbiddenResource]
    );

    const handleOnChangeDashboardUUID = useCallback(
        async (value: string) => {
            if (value) {
                setIsPanelLoading(true);
                await fetchNextInfiniteScroll();
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [currentDashboard?.key]
    );

    const handleAddChart = async () => {
        walletModalTrigger(() => {
            history.push(`/dashboard/${currentDashboard.key}/new-charts`);
        });
    };

    const fetchNextInfiniteScroll = async () => {
        const _infinityState: any = {
            ...infinityState,
        };

        _infinityState.items = [];
        _infinityState.count = 0;
        _infinityState.page = 0;
        globalThis.window.panelQueryToken = {};

        const fetchDashboardResponse = await dashboardService.getPanelDashboard(
            currentDashboard.key,
            {
                offset: NUMBER_OF_SCROLL * _infinityState.page,
                limit: NUMBER_OF_SCROLL,
            }
        );

        if (!isMountedRef.current) {
            return;
        }

        if (isHttpSuccess(fetchDashboardResponse.status)) {
            _infinityState.items = [
                ..._infinityState.items,
                ...(fetchDashboardResponse.data?.sort(
                    (a: any, b: any) => a.order - b.order
                ) || []),
            ];

            _infinityState.count = fetchDashboardResponse.total;

            _infinityState.hasMore =
                _infinityState.items.length < _infinityState.count;
            setInfinityState(_infinityState);
        } else {
            showErrorAlert(
                getAPIError(fetchDashboardResponse, FETCH_CHART_FAIL_MESSAGE)
            );
        }
        setIsPanelLoading(false);
    };

    const onGridChange = ({
        data: dataItems,
        items: targetItems,
    }: GridStackChangeEvent) => {
        const data = {
            order: {} as any,
            size: {} as any,
        };

        dataItems
            .sort((a: any, b: any) => {
                return a.y * 1e6 + a.x - (b.y * 1e6 + b.x);
            })
            .forEach((item: any, index: number) => {
                data.order[item.id] = index;
            });

        // For change items only
        if (!targetItems) return;
        targetItems.forEach((item: any) => {
            data.size[item.el.id] = item.w;
        });

        dashboardService.updateOrderPanel(currentDashboard.key, data);
    };

    const renderDashboardPanels = () => (
        <>
            {infinityState.items.length > 0 && renderPanels()}
            {infinityState.items.length === 0 && renderEmpty()}
        </>
    );

    const handleChangeWidgetDimension = (uuid: string, width: number) => {
        if (!uuid || !grid?.engine) {
            return;
        }

        const gridNode = grid?.engine.nodes.find(
            (node) => node?.el?.id === uuid
        );
        if (!gridNode?.el) {
            return;
        }

        grid.update(gridNode.el, { w: width });
        grid.engine.save(); // Recalculate internal positions
        grid.compact(); // Compact the grid to adjust any overlaps
    };

    const renderPanels = () => {
        return (
            <div className="w-100" ref={gridStackRef}>
                {!IS_BASIC_PLAN && (
                    <WalletAlertComponent showWarningWhen="LowBalance" />
                )}

                <GridStackElement
                    className="grid w-100"
                    id="dashboard-grid"
                    itemCount={infinityState.items.length}
                    gap={10}
                    cellHeight={CELL_HEIGHT}
                    reRender={false}
                    options={{
                        disableResize: false,
                        autoPosition: true,
                        alwaysShowResizeHandle: true,
                        resizable: {
                            handles: "e, w",
                        },
                        draggable: {
                            handle: ".widget-drag",
                        },
                    }}
                    onChange={onGridChange}
                    onInit={setGrid}
                >
                    {infinityState.items.map(
                        (
                            items: {
                                source: string;
                                chart: string;
                                attributes: ChartAttribute[];
                                uuid: string;
                                device_id: string;
                                query_time: number | null;
                                time_range: string;
                                name: string;
                            },
                            index: React.Key | null | undefined
                        ) => {
                            return (
                                    <Widget
                                        key={items.uuid}
                                        gridWrapper={gridStackRef}
                                        gatewaysConfig={gatewaysConfig}
                                        zoomLevel={zoomLevel}
                                        panel={items}
                                        dashboardId={currentDashboard.key}
                                        onDelete={handleOnChangeDashboardUUID}
                                        onSizeChange={handleChangeWidgetDimension}
                                        allAlerts={allAlerts}
                                        allRegistry={allRegistry}
                                    />
                            );
                        }
                    )}
                </GridStackElement>
            </div>
        );
    };

    const renderEmpty = () => (
        <Col sm="12" className="mt-2">
            <div className="empty-widget text-center">
                <h3 className="mb-3">No widgets added</h3>
                <h5 className="mb-3">Add a new widget to your dashboard.</h5>
                <HoverAuthorizeTooltip permission="dashboard:create">
                    <Button
                        variant="primary"
                        className="mt-3"
                        onClick={handleAddChart}
                    >
                        Add Widget
                    </Button>
                </HoverAuthorizeTooltip>
            </div>
        </Col>
    );

    useEffect(() => {
        const handleResize = () => {
            const newZoomLevel = window.devicePixelRatio;
            if (newZoomLevel !== zoomLevel) setZoomLevel(newZoomLevel);
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [zoomLevel]);
    useEffect(() => {
        if (currentDashboard?.key) {
            handleOnChangeDashboardUUID(currentDashboard.key);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentDashboard]);

    // Listen for localStorage changes to update jsonListLoaded state
    useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === "listOfJSON") {
                setJsonListLoaded(
                    LocalStorageService.getItem("listOfJSON") !== ""
                );
            }
        };

        globalThis.addEventListener("storage", handleStorageChange);
        return () =>
            globalThis.removeEventListener("storage", handleStorageChange);
    }, []);

    useEffect(() => {
        (async () => {
            if (!currentOrgId || !jsonListLoaded) return;

            const dashboardsResp = await dashboardService.getDashboards(
                currentOrgId
            );

            if (!isMountedRef.current) {
                return;
            }

            if (dashboardsResp?.status === HttpStatus.FORBIDDEN) {
                setIsForbiddenResource(true);
                return;
            }

            if (!isHttpSuccess(dashboardsResp?.status)) {
                showErrorAlert(
                    getAPIError(dashboardsResp, FETCH_DASHBOARD_FAIL_MESSAGE)
                );
                return;
            }
            setIsPanelLoading(!jsonListLoaded);
            setIsForbiddenResource(false);

            if (!LocalStorageService.getItem("dbInfo")) {
                LocalStorageService.setItem(
                    "dbInfo",
                    tagOptions(dashboardsResp.data)[0]
                );
            }

            const dbInfo = LocalStorageService.getItem("dbInfo");
            const hasActiveDashboard = dashboardsResp.data.some(
                (dashboard: DashboardModel) => dashboard.uuid === dbInfo?.key
            );

            if (hasActiveDashboard) {
                storeDispatchActions(
                    storeDashboardAction({
                        dashboardList: tagOptions(dashboardsResp.data),
                        targetId: dbInfo.key,
                    })
                );
            } else {
                storeDispatchActions(
                    storeDashboardAction({
                        dashboardList: tagOptions(dashboardsResp.data),
                    })
                );
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentOrgId, jsonListLoaded]);

    useEffect(() => {
        if (!currentOrgId || !jsonListLoaded) {
            return;
        }

        const checkPermission = () => {
            if (!hasPermission) {
                setIsDashboardLoading(false);
                return setIsForbiddenResource(true);
            }

            setIsDashboardLoading(false);
            setIsForbiddenResource(false);
        };

        checkPermission();
    }, [currentOrgId, jsonListLoaded, hasPermission]);

    const handleCloseDashboardModal = () => {
        setShowDashboardForm(false);
    };

    const updateDashboardState = async (dashboardId?: string) => {
        const getDashboardResp = await dashboardService.getDashboards(
            currentOrgId
        );

        if (!isHttpSuccess(getDashboardResp.status)) {
            showErrorAlert(
                getAPIError(
                    getDashboardResp,
                    "Unable to get Dashboards. Please try again."
                )
            );
            return;
        }

        storeDispatchActions(
            storeDashboardAction({
                dashboardList: tagOptions(getDashboardResp.data),
                targetId: dashboardId,
            })
        );

        handleCloseDashboardModal();
    };

    const createDashboard = async (name: string, color: string) => {
        const response = await dashboardService.addNewDashBoard(name, color);

        if (!isHttpSuccess(response.status)) {
            showErrorAlert(
                getAPIError(
                    response,
                    "Unable to add Dashboard. Please try again."
                )
            );
            return;
        }
        LocalStorageService.setItem("dbInfo", tagOptions([response.data]));

        showSuccessAlert({
            message: CREATED_DASHBOARD_MESSAGE,
        });

        await updateDashboardState(response.data.uuid);
    };

    const renderContent = () => {
        if (isPanelLoading) {
            return <ContentWrapper isLoading={isPanelLoading} />;
        }

        if (dashboardList.length > 0) {
            return renderDashboardPanels();
        }

        return (
            <Col sm="12" className="mt-2">
                <div className="empty-widget text-center">
                    <h3 className="mb-3">No Dashboards created yet</h3>
                    <h5 className="mb-3">
                        Create Dashboards to visualize your data.
                    </h5>
                    <HoverAuthorizeTooltip
                        permission="dashboard:create"
                        className="mt-3"
                    >
                        <Button
                            variant="primary"
                            onClick={() => {
                                if (
                                    IS_BASIC_PLAN &&
                                    !APP_CONFIG.APP_IS_ONPREM
                                ) {
                                    toggleShowBasicPlanModal();
                                } else {
                                    walletModalTrigger(() => {
                                        setShowDashboardForm(true);
                                        setFormType(FormType.CREATE);
                                    });
                                }
                            }}
                        >
                            Create New Dashboard
                        </Button>
                    </HoverAuthorizeTooltip>
                </div>
                <WalletAlertComponent />
                {IS_BASIC_PLAN && !APP_CONFIG.APP_IS_ONPREM && (
                    <BasicPlanModal
                        showBasicPlanModal={showBasicPlanModal}
                        toggleShowBasicPlanModal={toggleShowBasicPlanModal}
                    />
                )}
                <DashboardModal
                    show={showDashboardForm}
                    onClose={handleCloseDashboardModal}
                    type={formType}
                    onSubmit={createDashboard}
                />
            </Col>
        );
    };

    return (
        <ContentWrapper
            isForbiddenResource={isForbiddenResource}
            title="Dashboard"
            isLoading={isDashboardLoading}
        >
            {dashboardList.length !== 0 && (
                <div className="page-head">
                    <Container fluid>
                        <DashboardHeader />
                    </Container>
                </div>
            )}

            <div className="page-content">
                <Container fluid>
                    <Row>{renderContent()}</Row>
                </Container>
            </div>
        </ContentWrapper>
    );
};

export default Dashboard;
